import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, MessageSquarePlus, Send } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { submitFeedback } from '@/lib/feedback-api'
import { getErrorMessage } from '@/lib/error-messages'

const feedbackSchema = z.object({
  title: z.string().trim().min(1, 'Titel ist erforderlich').max(200, 'Maximal 200 Zeichen'),
  body: z.string().trim().min(1, 'Text ist erforderlich').max(5000, 'Maximal 5000 Zeichen'),
})

type FeedbackValues = z.infer<typeof feedbackSchema>

interface FeedbackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const form = useForm<FeedbackValues>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: { title: '', body: '' },
  })

  const submitMutation = useMutation({
    mutationFn: submitFeedback,
    onSuccess: () => {
      toast.success('Danke für dein Feedback!')
      form.reset()
      onOpenChange(false)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquarePlus className="h-5 w-5 text-primary" />
            Feedback senden
          </DialogTitle>
          <DialogDescription>
            Lob, Kritik oder Ideen? Dein Feedback geht direkt an das motionLAB-Team.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit((values) => submitMutation.mutate(values))} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Titel</FormLabel>
                  <FormControl>
                    <Input placeholder="Worum geht es?" maxLength={200} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="body"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nachricht</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Dein Feedback..."
                      className="min-h-[160px]"
                      maxLength={5000}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={submitMutation.isPending}>
                {submitMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sende...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" /> Feedback senden
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
